import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import statusService from '../services/statusService';
import * as eventService from '../services/eventService';
import * as friendsService from '../services/friendsService';
import { useNavigate } from 'react-router-dom';
import { 
  Circle, Zap, Moon, Coffee, MessageCircle, RefreshCw, User, Info,
  Star, Heart, Smile, Music, Gamepad2, Film, Book, Dumbbell, 
  Sparkles, Flame, Users, Home, Briefcase, GraduationCap, Plane, ShoppingCart,
  Calendar, Clock, MapPin, ArrowRight, Loader
} from 'lucide-react';

const STATUS_ICONS = {
  disponibile: Circle,
  occupato: Zap,
  non_disturbare: Moon,
  afk: Coffee,
  in_pausa: MessageCircle
};

const CUSTOM_STATUS_ICONS = {
  Star, Heart, Smile, Music, Gamepad2, Film, Book, Dumbbell,
  Sparkles, Flame, Users, Home, Briefcase, GraduationCap, Plane, ShoppingCart
};

const STATUS_COLORS = {
  disponibile: {
    bg: 'bg-green-500/20 dark:bg-green-400/20',
    border: 'border-green-500',
    text: 'text-green-600 dark:text-green-400',
    icon: 'text-green-600 dark:text-green-400'
  },
  occupato: {
    bg: 'bg-orange-500/20 dark:bg-orange-400/20',
    border: 'border-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    icon: 'text-orange-600 dark:text-orange-400'
  },
  non_disturbare: {
    bg: 'bg-red-500/20 dark:bg-red-400/20',
    border: 'border-red-500',
    text: 'text-red-600 dark:text-red-400',
    icon: 'text-red-600 dark:text-red-400'
  },
  afk: {
    bg: 'bg-yellow-500/20 dark:bg-yellow-600/20',
    border: 'border-yellow-600',
    text: 'text-yellow-700 dark:text-yellow-400',
    icon: 'text-yellow-700 dark:text-yellow-400'
  },
  in_pausa: {
    bg: 'bg-blue-500/20 dark:bg-blue-400/20',
    border: 'border-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
    icon: 'text-blue-600 dark:text-blue-400'
  }
};

const STATUS_LABELS = {
  disponibile: 'Disponibile',
  occupato: 'Occupato',
  non_disturbare: 'Non Disturbare',
  afk: 'AFK',
  in_pausa: 'In Pausa'
};

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const { connect, disconnect, joinBeccoRoom, on, connected, connectionError } = useSocket();
  const navigate = useNavigate();
  const [beccoInfo, setBeccoInfo] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      console.log('🔄 loadData started for user:', user.id);
      setError(null);
      
      // Carica info del Becco
      const beccoResponse = await statusService.getMyBecco(user.id);
      console.log('🐥 Becco response:', beccoResponse);
      const beccoData = beccoResponse.data || beccoResponse;
      console.log('🐥 Becco exists:', !!beccoData.becco, 'Becco ID:', beccoData.becco?.id);
      setBeccoInfo(beccoData.becco);
      
      // Carica stato corrente del Becco
      if (beccoData.becco) {
        const statusResponse = await statusService.getCurrentStatus(beccoData.becco.id);
        console.log('📊 Status response:', statusResponse);
        const statusData = statusResponse.data || statusResponse;
        console.log('📊 Status data:', statusData);
        if (statusData.customStatus) {
          console.log('📊 Custom status details:', statusData.customStatus);
        }
        setCurrentStatus(statusData);

        // Carica eventi prossimi (max 3)
        await loadUpcomingEvents(beccoData.becco.id);
      } else {
        console.warn('⚠️ No becco found for user:', user.id);
      }
    } catch (err) {
      console.error('Errore nel caricamento dati:', err);
      setError(err.response?.data?.error || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadUpcomingEvents = async (beccoId) => {
    try {
      const eventsResponse = await eventService.getEvents(beccoId, { upcoming: true });
      console.log('📅 Events response:', eventsResponse);
      
      // Estrai l'array di eventi dalla risposta
      const allEvents = eventsResponse.data || [];
      
      const userEvents = allEvents.filter(event => {
        const isInvited = event.participants?.some(p => p.userId === user.id);
        return isInvited || event.isPublic;
      });
      
      console.log('📅 User events:', userEvents.length);
      
      // Prendi solo i primi 3 eventi
      const sortedEvents = userEvents.sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
      setEvents(sortedEvents.slice(0, 3));
    } catch (error) {
      console.error('Errore caricamento eventi:', error);
      setEvents([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  useEffect(() => {
    console.log('👤 useEffect triggered - user:', user, 'user.id:', user?.id);
    if (user?.id) {
      console.log('✅ User ID present, calling loadData...');
      loadData();
      
      // Connetti al WebSocket
      const token = localStorage.getItem('token');
      if (token) {
        console.log('Connecting to WebSocket...');
        connect(token, false);
      }
      
      // Auto-refresh ogni 30 secondi (fallback se WebSocket non funziona)
      const interval = setInterval(loadData, 30000);
      
      return () => {
        clearInterval(interval);
        disconnect();
      };
    } else {
      console.warn('❌ User ID not available, skipping loadData');
    }
  }, [user?.id, connect, disconnect]);

  // Effetto separato per joinare la room del Becco
  useEffect(() => {
    if (beccoInfo?.id && connected) {
      console.log('Joining Becco room:', beccoInfo.id);
      joinBeccoRoom(beccoInfo.id);
    }
  }, [beccoInfo?.id, connected, joinBeccoRoom]);

  // Effetto separato per WebSocket events (solo una volta)
  useEffect(() => {
    console.log('🔔 Setting up WebSocket event listeners');

    // Handler per aggiornamenti stato
    const handleStatusUpdate = async (data) => {
      console.log('🔔 Status update received via WebSocket:', data);
      
      // Se è uno stato custom e abbiamo i dettagli completi nel payload
      if (data.status === 'custom' && data.customStatus) {
        console.log('✅ Custom status with full details:', data.customStatus);
        setCurrentStatus({
          status: data.status,
          customStatusId: data.customStatusId,
          customMessage: data.customMessage,
          lastUpdated: data.lastUpdated,
          customStatus: data.customStatus
        });
      } else if (data.status === 'custom' && beccoInfo?.id) {
        // Fallback: se non abbiamo i dettagli, ricarica dal server
        console.log('⚠️ Custom status without details, fetching from server...');
        try {
          const statusData = await statusService.getCurrentStatus(beccoInfo.id);
          setCurrentStatus(statusData);
        } catch (error) {
          console.error('Error reloading custom status:', error);
        }
      } else {
        // Stato predefinito
        console.log('✅ Standard status:', data.status);
        setCurrentStatus({
          status: data.status,
          customStatusId: data.customStatusId,
          customMessage: data.customMessage,
          lastUpdated: data.lastUpdated
        });
      }
    };

    // Subscribe agli eventi
    const unsubscribeStatus = on('status-updated', handleStatusUpdate);
    const unsubscribeRoom = on('room-joined', (data) => {
      console.log('🔔 Joined room:', data.room);
    });

    console.log('🔔 WebSocket event listeners registered');

    // Cleanup
    return () => {
      console.log('🔔 Cleaning up WebSocket event listeners');
      if (unsubscribeStatus) unsubscribeStatus();
      if (unsubscribeRoom) unsubscribeRoom();
    };
  }, [on, beccoInfo]);

  if (loading) {
    return (
      <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-light-secondary dark:border-dark-secondary mx-auto mb-4"></div>
          <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center p-4">
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-6 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4 text-light-error dark:text-dark-error">
            <Info size={24} />
            <h2 className="text-lg font-semibold">Errore</h2>
          </div>
          <p className="text-light-on-surface dark:text-dark-on-surface mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="w-full px-4 py-2 bg-light-secondary dark:bg-dark-secondary text-light-on-secondary dark:text-dark-on-secondary rounded-lg hover:opacity-90 transition-opacity"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  if (!beccoInfo) {
    return (
      <div className="min-h-screen bg-light-surface dark:bg-dark-surface flex items-center justify-center p-4">
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-6 max-w-md w-full text-center">
          <Info size={48} className="mx-auto mb-4 text-light-on-surface-variant dark:text-dark-on-surface-variant" />
          <h2 className="text-xl font-semibold text-light-on-surface dark:text-dark-on-surface mb-2">
            Nessun Becco Associato
          </h2>
          <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant">
            Non sei ancora associato a nessun Becco. Contatta un amministratore per essere aggiunto.
          </p>
        </div>
      </div>
    );
  }

  const statusKey = currentStatus?.customStatusId 
    ? 'custom' 
    : (currentStatus?.status || 'disponibile').replace(/-/g, '_');
  console.log('Current status:', currentStatus);
  console.log('Status key:', statusKey);
  
  // Per stati custom, usa i colori personalizzati
  const customColor = currentStatus?.customStatus?.color;
  const colors = currentStatus?.customStatusId && customColor
    ? {
        bg: 'bg-light-surface-container dark:bg-dark-surface-container',
        border: 'border-light-outline dark:border-dark-outline',
        text: 'text-light-on-surface dark:text-dark-on-surface',
        icon: ''
      }
    : (STATUS_COLORS[statusKey] || STATUS_COLORS.disponibile);
  
  const StatusIcon = STATUS_ICONS[statusKey] || Circle;
  
  // Custom icon solo se è uno status custom
  let CustomIcon = null;
  if (currentStatus?.customStatusId && currentStatus?.customStatus) {
    const customIconName = currentStatus.customStatus.iconName;
    console.log('🎨 Custom status icon:', customIconName);
    CustomIcon = CUSTOM_STATUS_ICONS[customIconName] || Star;
  }

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header con info Becco */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
          animate-fade-in opacity-0"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-light-secondary/20 dark:bg-dark-secondary/20 rounded-full">
                <User className="text-light-secondary dark:text-dark-secondary" size={24} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-light-on-surface dark:text-dark-on-surface">
                  {beccoInfo.nome} {beccoInfo.cognome}
                </h1>
                <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                  @{beccoInfo.username}
                </p>
              </div>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg bg-light-surface-variant dark:bg-dark-surface-variant hover:bg-light-surface-container-high dark:hover:bg-dark-surface-container-high transition-colors disabled:opacity-50"
              title="Aggiorna stato"
            >
              <RefreshCw size={20} className={`text-light-on-surface dark:text-dark-on-surface ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stato Corrente */}
        <div className={`rounded-lg shadow-surface-container p-6 border-l-4 ${colors.bg} ${colors.border}
          animate-fade-in opacity-0`}
          style={{
            ...(currentStatus?.customStatusId && customColor ? { borderLeftColor: customColor } : {}),
            animationDelay: '200ms',
            animationFillMode: 'forwards'
          }}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-full ${colors.icon}`}
              style={currentStatus?.customStatusId && customColor 
                ? { backgroundColor: `${customColor}20` } 
                : {}}>
              {currentStatus?.customStatusId ? (
                <CustomIcon 
                  size={28} 
                  style={{ color: customColor }} 
                />
              ) : (
                <StatusIcon size={28} />
              )}
            </div>
            <div className="flex-1">
              <h2 className={`text-2xl font-bold ${colors.text}`}>
                {currentStatus?.customStatusId 
                  ? currentStatus.customStatus?.label 
                  : STATUS_LABELS[statusKey] || 'Disponibile'}
              </h2>
              <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Aggiornato {new Date(currentStatus?.lastUpdated).toLocaleString('it-IT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Descrizione Stato */}
          {currentStatus?.description && (
            <div className="mb-4 p-4 bg-light-surface dark:bg-dark-surface rounded-lg">
              <p className="text-light-on-surface dark:text-dark-on-surface">
                {currentStatus.description}
              </p>
            </div>
          )}

          {/* Messaggio Personalizzato */}
          {currentStatus?.customMessage && (
            <div className="relative p-4 bg-light-surface dark:bg-dark-surface rounded-lg border-2 border-light-outline dark:border-dark-outline">
              <div className="absolute -top-3 left-4 px-2 bg-light-surface dark:bg-dark-surface">
                <span className="text-xs font-semibold text-light-on-surface-variant dark:text-dark-on-surface-variant">
                  Messaggio
                </span>
              </div>
              <p className="text-light-on-surface dark:text-dark-on-surface italic">
                "{currentStatus.customMessage}"
              </p>
            </div>
          )}

          {/* Messaggio Custom Status */}
          {currentStatus?.customStatusId && currentStatus.customStatus?.message && (
            <div className="mt-4 p-4 bg-light-surface dark:bg-dark-surface rounded-lg">
              <p className="text-light-on-surface dark:text-dark-on-surface">
                {currentStatus.customStatus.message}
              </p>
            </div>
          )}
        </div>

        {/* Prossimi Eventi */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
          animate-fade-in opacity-0"
          style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg sm:text-xl font-bold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-light-secondary dark:text-dark-secondary" />
              Prossimi Eventi
            </h3>
            {events.length > 0 && (
              <button
                onClick={() => navigate('/eventi')}
                className="text-sm font-medium text-light-secondary dark:text-dark-secondary hover:underline flex items-center gap-1"
              >
                Vedi tutti
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {events.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-30" />
              <p className="text-sm sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Nessun evento in programma
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  onClick={() => navigate('/eventi')}
                  className="p-3 sm:p-4 rounded-lg bg-light-surface dark:bg-dark-surface 
                    border border-light-outline dark:border-dark-outline
                    hover:shadow-md hover:border-light-secondary dark:hover:border-dark-secondary
                    transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm sm:text-base text-light-on-surface dark:text-dark-on-surface mb-1">
                        {event.title}
                      </h4>
                      
                      {/* Descrizione evento */}
                      {event.description && (
                        <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-2 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{new Date(event.eventDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </div>
                        {event.startTime && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span>{event.startTime.substring(0, 5)}</span>
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center gap-1 min-w-0">
                            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Numero partecipanti */}
                      {event.participants && event.participants.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">
                          <Users className="w-3 h-3" />
                          <span>{event.participants.length} partecipant{event.participants.length !== 1 ? 'i' : 'e'}</span>
                          {event.maxParticipants && (
                            <span>/ {event.maxParticipants}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-light-on-surface-variant dark:text-dark-on-surface-variant flex-shrink-0 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Auto-refresh */}
        <div className="text-center text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
          <p>Lo stato si aggiorna automaticamente ogni 30 secondi</p>
        </div>
      </div>
    </div>
  );
}
